import { useRef, useEffect, useState } from "react";
import LazyVideo from "@/components/LazyVideo";
import { GraduationCap, Users, Baby, ArrowUpRight } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {const obs = new IntersectionObserver(([e]) => {if (e.isIntersecting) setInView(true);}, { threshold: 0.15 });if (ref.current) obs.observe(ref.current);return () => obs.disconnect();}, [ref]);
  return inView;
}

const ACADEMY_VIDEO = "/assets/video/academia.mp4";

const cards = [
{ icon: GraduationCap, title: "Aulas Individuais", desc: "Treinos personalizados com instrutor.", price: "A partir de 25€" },
{ icon: Users, title: "Aulas de Grupo", desc: "Grupos de 3-4 jogadores.", price: "Grupos 2-4 desde 10€" },
{ icon: Baby, title: "Escola Kids", desc: "Programa para crianças dos 6 aos 16.", price: "Dos 6 aos 16" }];


export default function Academy() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section id="academy" data-testid="academy-section" ref={ref} className="py-24 bg-[#0d1320]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`${inView ? 'fade-in-up' : 'opacity-0'}`}>
          
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Academia</span>
            <h2 className="font-display text-3xl text-white font-medium leading-tight mb-6">Evolui o teu jogo.</h2>
            
            {/* Video Mobile */}
            <div className="relative w-full mb-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                <div className="aspect-[9/16]">
                  <LazyVideo
                    src={ACADEMY_VIDEO}
                    className="w-full h-full object-cover"
                    data-testid="academy-video-mobile" />
                </div>
              </div>
            </div>
            
            {/* Cards Mobile */}
            <div className="space-y-3">
              {cards.map((c, i) =>
                <div key={c.title} className="bg-[#0a0f1a] border border-white/5 rounded-xl p-5 flex gap-4 items-start hover:border-jp-lime/15 transition-colors" data-testid={`academy-card-mobile-${i}`}>
                  <c.icon className="w-6 h-6 shrink-0 text-jp-lime mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="font-body font-medium text-base text-white">{c.title}</div>
                    <div className="font-body text-base text-white/40 mt-1">{c.desc}</div>
                    <div className="font-body text-base font-medium text-jp-lime mt-2">{c.price}</div>
                  </div>
                </div>
              )}
            </div>
            <a
              href="#contacts"
              className="mt-8 w-full bg-jp-lime text-black font-body font-semibold text-sm px-6 py-4 rounded-full hover:bg-jp-lime-dark transition-all flex items-center justify-center gap-2 group"
              data-testid="academy-cta-mobile"
            >
              Saber mais
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>

          {/* Desktop Layout: Video left, Content right */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-16 items-center">
            {/* Video on the left */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-[320px]">
                  <div className="aspect-[9/16]">
                    <LazyVideo
                    src={ACADEMY_VIDEO}
                      className="w-full h-full object-cover"
                    data-testid="academy-video" />
                  </div>
                </div>
              </div>
            </div>

            {/* Content on the right */}
            <div className="lg:col-span-7">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Academia</span>
              <h2 className="font-display text-4xl sm:text-5xl text-white font-medium leading-tight mb-8">Evolui o teu jogo.</h2>
              <div className="space-y-3">
                {cards.map((c, i) =>
                  <div key={c.title} className="bg-[#0a0f1a] border border-white/5 rounded-xl p-6 flex gap-4 items-start hover:border-jp-lime/15 transition-colors" data-testid={`academy-card-${i}`}>
                    <c.icon className="w-7 h-7 shrink-0 text-jp-lime mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="font-body font-medium text-lg text-white">{c.title}</div>
                      <div className="font-body text-base text-white/40 mt-1">{c.desc}</div>
                      <div className="font-body text-base font-medium text-jp-lime mt-2">{c.price}</div>
                    </div>
                  </div>
                )}
              </div>
              <a
                href="#contacts"
                className="inline-flex mt-8 bg-jp-lime text-black font-body font-semibold text-sm px-8 py-4 rounded-full hover:bg-jp-lime-dark transition-all items-center gap-2 group"
                data-testid="academy-cta"
              >
                Saber mais
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);
}
